import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put } from '@/shared/api/api-client';
import type {
  DoctorSetupDetail,
  DoctorSetupDetailsResponse,
  EPrescriptionLogin,
} from '@/types/settings-api';

/**
 * Practitioner Account Details (the doctor record) — PROD-761.
 *
 * GET  /api/Doctor/DoctorSetupDetails/{doctorSetupId} — fetch wrapper with
 *      `doctorSetupDetail` + per-practice `practiceWorkingHours` (read-only).
 * PUT  /api/Doctor/{doctorSetupId} — save (body = just the `doctorSetupDetail`
 *      payload, NOT the wrapper).
 *
 * Wire-shape verified against working dev cURLs 2026-05-13.
 *
 * Wire-shape notes (do NOT regress):
 * - `role` is an integer (small enum), e.g. 6 = In-house Practitioner.
 * - `doctorSpecialities[]` + `doctorSpecialityIds[]` are a parallel pair
 *   — backend joins via both. Mirrors the Staff spec parity invariant.
 * - `specialities` (singular) is the PRIMARY speciality with the full
 *   SNOMED code; `speciality` is its GUID. Both must agree on save.
 * - `isLastLogin` is preserved verbatim on the wire (Flutter sends it back
 *   even though it's server-controlled); the backend ignores it on PUT.
 *   Mirroring Flutter for compatibility; can scrub to null in a follow-up
 *   if PHI audit flags it (per PROD-765 review precedent).
 * - `photoURL` is uppercase URL (backend field name verbatim).
 */
export function useDoctorSetupDetails(doctorSetupId: string | null | undefined) {
  return useQuery({
    queryKey: ['settings', 'doctor-setup-details', doctorSetupId],
    queryFn: ({ signal }) => {
      if (!doctorSetupId) {
        throw new Error('useDoctorSetupDetails called without doctorSetupId');
      }
      return get<DoctorSetupDetailsResponse>(
        `/api/Doctor/DoctorSetupDetails/${doctorSetupId}`,
        { signal },
      );
    },
    enabled: !!doctorSetupId,
  });
}

export function useUpdateDoctor() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (detail: DoctorSetupDetail) =>
      put<unknown>(`/api/Doctor/${detail.doctorSetupId}`, detail),
    onSuccess: (_data, detail) => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'doctor-setup-details', detail.doctorSetupId],
      });
      queryClient.invalidateQueries({ queryKey: ['settings', 'practitioners-list'] });
    },
  });
}

/**
 * EPrescription logins — per-practice eRx credentials for this provider.
 * Surfaced on the Account Details tab as a list of practice rows with
 * username + password fields.
 *
 * GET  /api/Doctor/EPrescriptionLogin?providerId={id}  — list
 * POST /api/Doctor/AddUpdateEPrescriptionLogin         — upsert single row
 */
export function useEPrescriptionLogins(providerId: string | null | undefined) {
  return useQuery({
    queryKey: ['settings', 'eprescription-logins', providerId],
    queryFn: ({ signal }) => {
      if (!providerId) {
        throw new Error('useEPrescriptionLogins called without providerId');
      }
      const qs = new URLSearchParams({ providerId });
      return get<EPrescriptionLogin[]>(
        `/api/Doctor/EPrescriptionLogin?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!providerId,
  });
}

export function useSaveEPrescriptionLogin() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (login: EPrescriptionLogin) =>
      post<unknown>('/api/Doctor/AddUpdateEPrescriptionLogin', login),
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({
        queryKey: ['settings', 'eprescription-logins', vars.providerId],
      });
    },
  });
}
